/**
 * What time it is, in the world.
 *
 * Orbital phase used to start from zero whenever a view was opened, so every
 * planet snapped back to the same place each time you entered a system and the
 * world had no continuity — leaving and returning rewound it.
 *
 * Phase is now a function of wall-clock time against a fixed epoch, which makes
 * a system's arrangement a fact about *when* rather than about how long you
 * have been looking at it. Two players in the same system at the same moment
 * see the same sky, without anything being synchronised.
 */
/**
 * The instant the universe started, in ms.
 *
 * Arbitrary, but fixed and past: phase is measured from here, so changing it
 * teleports every orbiting body. Chosen to be recent enough that the elapsed
 * seconds stay comfortably inside a double's exact-integer range for the
 * lifetime of the game.
 */
export const WORLD_EPOCH_MS = Date.UTC(2025, 0, 1);
/**
 * How much slower a system runs than the map.
 *
 * The map wants motion you can see: it is a chart, and watching a planet crawl
 * teaches nothing. Flight wants the opposite — at map speed a station drifts
 * out from under you while you are lining up on its pad, which turns docking
 * into chasing.
 *
 * Four hundred is slow enough that a station is effectively a fixed platform
 * over the minutes an approach takes, while a system watched for long enough
 * still visibly turns. Orbits stay real; they just stop being something you
 * have to fly against.
 */
export const FLIGHT_TIME_SCALE = 1 / 400;
/** Seconds since the epoch. The map's clock. */
export function worldTime(now = Date.now()) {
    return (now - WORLD_EPOCH_MS) / 1000;
}
/**
 * Seconds since the epoch, slowed to flight pace.
 *
 * Applied to the *absolute* time rather than to each frame's delta, so it stays
 * a pure function of the clock — a view that opened a minute ago and one that
 * just opened agree about where everything is.
 */
export function flightTime(now = Date.now()) {
    return worldTime(now) * FLIGHT_TIME_SCALE;
}
/**
 * How long a station's shelves stay picked over, in seconds.
 *
 * Two hours, and the slowest thing in the game to come round — a mission board
 * turns over in one. Everything a station sells shares it: the trader's goods,
 * its plating and its paint. One window rather than three, because "come back
 * later" is a thing a player learns once about a station rather than per counter.
 *
 * It lives here rather than with any one shop for the same reason.
 */
export const RESTOCK = 7200;
/**
 * Which restocking period a moment falls in.
 *
 * What has been bought is stored against this, so a rollover discards the record
 * rather than needing anything to run on a timer.
 */
export function shelfEra(now = Date.now()) {
    return Math.floor(worldTime(now) / RESTOCK);
}
/** Seconds in a day and a week, for the reward eras below. */
const DAY = 86_400;
const WEEK = 604_800;
/**
 * Which day a moment falls in, counted from the epoch.
 *
 * The same trick as `shelfEra`, and the reason login rewards need no timer, no
 * scheduled job and no timezone handling: `WORLD_EPOCH_MS` is midnight UTC
 * exactly, so this rolls over at midnight UTC and a claim is simply a row keyed
 * on the number.
 *
 * Deliberately *not* the player's local midnight. A timezone is something the
 * client chooses, so a reward that trusted it could be collected as many times
 * in a day as somebody had patience for.
 */
export function dayEra(now = Date.now()) {
    return Math.floor(worldTime(now) / DAY);
}
/**
 * How far the week count is shifted so weeks begin on Monday.
 *
 * The epoch is Wednesday 1 January 2025, so dividing elapsed time by a week
 * would put the boundary on Wednesday mornings. Counting instead from the Monday
 * before it — 30 December 2024 — is two days earlier, which is two days *added*
 * to the elapsed time.
 *
 * Added rather than subtracted from the result on purpose: the era is a stored
 * key, and keeping it positive and monotonic means a row written today sorts and
 * compares against one written at launch without anybody thinking about signs.
 */
const WEEK_OFFSET = 2 * DAY;
/**
 * Which week a moment falls in, beginning Monday.
 *
 * One constant decides this and getting it wrong is a whole week out, in a way
 * nothing would notice until somebody was paid on the wrong day — so the
 * boundary is pinned by tests either side of a rollover rather than reasoned
 * about here.
 */
export function weekEra(now = Date.now()) {
    return Math.floor((worldTime(now) + WEEK_OFFSET) / WEEK);
}
//# sourceMappingURL=clock.js.map